import { myEnabledFeatures, listPublicFeatures } from '@/api/feature'

const CACHE_KEY_PREFIX = 'enabledFeatureCodes:'
const FEATURE_STATUS_CACHE_KEY = 'featureStatusMap'

// 记录本次页面会话中已从服务器拉取过的 key，避免永远读旧缓存
const _fetchedThisSession = new Set()
let _featureStatusFetched = false

function buildContextKey(courseTypeCode, subjectCode) {
  const ct = (courseTypeCode || '').trim().toUpperCase() || 'GLOBAL'
  const sc = (subjectCode || '').trim().toUpperCase() || 'ALL'
  return `${ct}:${sc}`
}

export function getCourseTypeCodeFromContext(route) {
  const q = (route && route.query) || {}
  const fromQuery = q.courseTypeCode || q.courseType || q.ct
  if (fromQuery) return String(fromQuery).trim().toUpperCase()
  const stored = localStorage.getItem('selectedCourseTypeCode') || localStorage.getItem('currentCourseTypeCode')
  return stored ? String(stored).trim().toUpperCase() : ''
}

export function getCachedEnabledFeatures(courseTypeCode, subjectCode) {
  const raw = localStorage.getItem(CACHE_KEY_PREFIX + buildContextKey(courseTypeCode, subjectCode))
  if (raw === null || raw === undefined) return null
  try {
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch (e) {
    return []
  }
}

export function setCachedEnabledFeatures(courseTypeCode, subjectCode, codes) {
  localStorage.setItem(CACHE_KEY_PREFIX + buildContextKey(courseTypeCode, subjectCode), JSON.stringify(Array.isArray(codes) ? codes : []))
}

export function clearCachedEnabledFeatures() {
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith(CACHE_KEY_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k))
  localStorage.removeItem(FEATURE_STATUS_CACHE_KEY)
  try { localStorage.removeItem(FEATURE_LIMIT_CACHE_KEY) } catch (_) {}
  _fetchedThisSession.clear()
  _featureStatusFetched = false
}

export function getCachedFeatureStatusMap() {
  const raw = localStorage.getItem(FEATURE_STATUS_CACHE_KEY)
  if (raw === null || raw === undefined) return null
  try {
    const obj = JSON.parse(raw)
    return obj && typeof obj === 'object' ? obj : {}
  } catch (e) {
    return {}
  }
}

export function setCachedFeatureStatusMap(map) {
  localStorage.setItem(FEATURE_STATUS_CACHE_KEY, JSON.stringify(map && typeof map === 'object' ? map : {}))
}

const FEATURE_LIMIT_CACHE_KEY = 'featureLimitMap'

export async function ensureFeatureStatusMap(forceRefresh) {
  if (!forceRefresh && _featureStatusFetched) {
    const cached = getCachedFeatureStatusMap()
    if (cached !== null) return cached
  }
  const res = await listPublicFeatures()
  const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
  const map = {}
  const limitMap = {}
  list.forEach(item => {
    const code = String(item && item.featureCode || '').trim().toUpperCase()
    if (!code) return
    map[code] = Number(item.status) === 1
    // 缓存限制配置
    if (item.limitConfig) {
      try {
        const lc = typeof item.limitConfig === 'string' ? JSON.parse(item.limitConfig) : item.limitConfig
        if (lc && (Number(lc.freeLimit) > 0 || Number(lc.vipLimit) > 0)) {
          limitMap[code] = lc
        }
      } catch (_) {}
    }
  })
  setCachedFeatureStatusMap(map)
  try { localStorage.setItem(FEATURE_LIMIT_CACHE_KEY, JSON.stringify(limitMap)) } catch (_) {}
  _featureStatusFetched = true
  return map
}

export function isFeatureActive(featureCode) {
  const code = String(featureCode || '').trim().toUpperCase()
  if (!code) return true
  const map = getCachedFeatureStatusMap() || {}
  if (!Object.prototype.hasOwnProperty.call(map, code)) return true
  return !!map[code]
}

export async function ensureEnabledFeatures(courseTypeCode, subjectCode, forceRefresh) {
  const ct = (courseTypeCode || '').trim().toUpperCase() || ''
  const sc = (subjectCode || '').trim().toUpperCase() || ''
  const contextKey = buildContextKey(ct, sc)

  // 本次页面会话内已拉取过且未要求强制刷新 → 直接用缓存
  if (!forceRefresh && _fetchedThisSession.has(contextKey)) {
    const cached = getCachedEnabledFeatures(ct, sc)
    if (cached !== null) return cached
  }

  // 首次调用 / 强制刷新 → 从服务器获取
  const res = await myEnabledFeatures({ courseTypeCode: ct || undefined, subjectCode: sc || undefined })
  const codes = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
  setCachedEnabledFeatures(ct, sc, codes)
  _fetchedThisSession.add(contextKey)
  return codes
}

export function hasFeature(featureCode, courseTypeCode, subjectCode) {
  if (!featureCode) return true
  const list = getCachedEnabledFeatures(courseTypeCode, subjectCode) || []
  return list.includes(featureCode)
}

/**
 * 获取某功能的限制配置（从缓存读取）
 * @param {string} featureCode 功能编码
 * @returns {{ freeLimit: number, vipLimit: number, limitLabel: string } | null}
 */
export function getFeatureLimitConfig(featureCode) {
  const code = String(featureCode || '').trim().toUpperCase()
  if (!code) return null
  try {
    const raw = localStorage.getItem(FEATURE_LIMIT_CACHE_KEY)
    if (!raw) return null
    const map = JSON.parse(raw)
    return (map && map[code]) ? map[code] : null
  } catch (_) {
    return null
  }
}

/**
 * 获取当前用户在某功能下的使用数量限制
 * @param {string} featureCode 功能编码
 * @param {boolean} isVip 当前用户是否是会员
 * @returns {number} 可用数量限制，0 表示不限制，>0 表示最多可访问的数量
 */
export function getFeatureLimit(featureCode, isVip) {
  const config = getFeatureLimitConfig(featureCode)
  if (!config) return 0
  const limit = isVip ? Number(config.vipLimit) : Number(config.freeLimit)
  return (limit > 0) ? limit : 0
}

/**
 * 获取某功能的限制说明文本
 * @param {string} featureCode
 * @returns {string}
 */
export function getFeatureLimitLabel(featureCode) {
  const config = getFeatureLimitConfig(featureCode)
  return (config && config.limitLabel) ? String(config.limitLabel) : ''
}
